import { endOfToday, isAfter, isFuture, subMonths } from "date-fns";

import Role from "constants/role";
import { SubscriptionPhase } from "constants/subscription-phase";
import { IBilling } from "store/entities/billing";
import { IRecurlyCoupon } from "store/entities/recurly";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { BillingCycle, ISubscription, Plan, Status } from "store/entities/subscriptions";

export function isActive(subscription: ISubscription | null): boolean {
  return Boolean(subscription && subscription.status === Status.Active);
}

export function accountIsCanceled(
  billing: IBilling | null,
  isShopifyTrial = false,
  isManagedByShopify = false
): boolean {
  return (!billing || billing.state !== "active") && !isShopifyTrial && !isManagedByShopify;
}

export function isExpired(subscription: ISubscription | null): boolean {
  return subscription === null || subscription.status === Status.Expired;
}

export function isPaidSubscription(subscriptionPhase: SubscriptionPhase): boolean {
  return [SubscriptionPhase.Paid, SubscriptionPhase.PaidNoCC].includes(subscriptionPhase);
}

export function isSubscribed(billing: IBilling | null): boolean {
  return Boolean(billing && billing.billingInfo && billing.billingInfo.creditCard);
}

export function hasSubscription(billing: IBilling | null, subscription: ISubscription | null): boolean {
  return isSubscribed(billing) && !isExpired(subscription);
}

export function inTrial(subscription: ISubscription | null, isShopifyTrial = false): boolean {
  return (
    Boolean(
      subscription?.trialExpiresAt &&
        isFuture(subscription.subscriptionExpiresAt) &&
        isFuture(subscription.trialExpiresAt)
    ) || isShopifyTrial
  );
}

export function getSubscriptionPhase(subscription: ISubscription | null, billing: IBilling | null): SubscriptionPhase {
  if (accountIsCanceled(billing) || isExpired(subscription)) {
    if (subscription?.trialExpiresAt && isAfter(subscription.subscriptionExpiresAt, subscription?.trialExpiresAt)) {
      return SubscriptionPhase.ExpiredPaid;
    }
    return SubscriptionPhase.ExpiredTrial;
  }

  const subscribed = isSubscribed(billing);
  if (inTrial(subscription)) {
    return subscribed ? SubscriptionPhase.TrialCC : SubscriptionPhase.TrialNoCC;
  }

  return subscribed ? SubscriptionPhase.Paid : SubscriptionPhase.PaidNoCC;
}

export function buildPlanCode(plan: Plan, billingCycle: BillingCycle): string {
  return `hd-${plan}${billingCycle}m`;
}

export function areDifferent(oldSubscription: ISubscription, newSubscription: Partial<ISubscription>): boolean {
  return (
    oldSubscription.agents !== newSubscription.agents ||
    oldSubscription.billingCycle !== newSubscription.billingCycle ||
    oldSubscription.plan !== newSubscription.plan
  );
}

export function changeIsValid(
  billing: IBilling | null,
  oldSubscription: ISubscription,
  newSubscription: ISubscription,
  coupon: IRecurlyCoupon | null,
  nonViewersCount: number
): boolean {
  if (newSubscription.agents < nonViewersCount) {
    return false;
  }

  return (
    !hasSubscription(billing, oldSubscription) ||
    areDifferent(oldSubscription, newSubscription) ||
    (coupon !== null && coupon.redeemed === false)
  );
}

export function getAgentsForSubscriptionPhase(
  phase: SubscriptionPhase,
  subscriptionAgents: number,
  nonViewersCount: number
): number {
  if (phase === SubscriptionPhase.TrialNoCC) {
    return nonViewersCount;
  }
  return Math.max(subscriptionAgents, nonViewersCount);
}

export function formatPriceChange(change: number): string {
  if (change === 0) {
    return `$${change}`;
  }
  if (change > 0) {
    return `+ $${change.toFixed(2)}`;
  }
  return `- $${Math.abs(change).toFixed(2)}`;
}

export function calculateNewSubscription(
  nonViewersCount: number,
  newAgentRoles: Role[],
  oldSubscription: ISubscription
): ISubscription {
  const paidAgentsToAdd = newAgentRoles.filter(role => role !== Role.Viewer).length;
  return {
    ...oldSubscription,
    agents: Math.max(oldSubscription.agents, nonViewersCount + paidAgentsToAdd),
  };
}

export function doesNewSubscriptionRequirePayment(
  oldSubscription: ISubscription,
  newSubscription: ISubscription
): boolean {
  if (inTrial(oldSubscription)) {
    return false;
  }
  if (isExpired(oldSubscription)) {
    return true;
  }
  return newSubscription.agents > oldSubscription.agents;
}

export function getNewSubscriptionDefaultState(
  subscription: ISubscription | null,
  billing: IBilling | null,
  nonViewersCount: number
): ISubscription {
  if (subscription === null) {
    return {
      id: "0",
      plan: Plan.Team,
      billingCycle: BillingCycle.Monthly,
      agents: nonViewersCount,
      status: Status.Active,
      currentPeriodStartsAt: subMonths(endOfToday(), 1),
      currentPeriodEndsAt: endOfToday(),
      trialExpiresAt: endOfToday(),
      subscriptionExpiresAt: endOfToday(),
      isCanceled: true,
    };
  }

  const phase = getSubscriptionPhase(subscription, billing);
  const agents = getAgentsForSubscriptionPhase(phase, subscription.agents, nonViewersCount);

  return {
    ...subscription,
    agents,
  };
}

export function willUpgradeSubscription(
  subscription: ISubscription,
  billing: IBilling | null,
  currentNonViewers: number,
  nonViewersToAdd: number,
  isManaged: boolean
): boolean {
  const phase = getSubscriptionPhase(subscription, billing);
  if (phase === SubscriptionPhase.TrialNoCC) {
    return false;
  }

  const limit = subscription.agents - currentNonViewers;
  const exceedsLimit = nonViewersToAdd > limit;
  if (!exceedsLimit) {
    return false;
  }

  if (phase === SubscriptionPhase.TrialCC) {
    return isManaged;
  }
  return true;
}

export const formatPrice = (price: number): string => {
  return `$${price.toFixed(2).replace(/\.00/, "")}`;
};
